// Disclosure hazırlık — /clients/kpi/disclosure (M16 3. dalga, `kpi_metric_requirements` ×
// `services/kpi/disclosure.ts`; güvenlik/UX taraması 2026-10-08 Codex #4 — eski "TODO-KPI-08"
// başlığı kapanmış işi anlatıyordu). Standart seçici (GRI/TSRS/SPK…), yıl, özet kartlar (% hazır),
// sekmeler: Eksikler / Tüm eşlemeler / Sette olmayan gerekli göstergeler (→ "Sete ekle" =
// addSetIndicator → grid yenilenir). Satıra tıkla → Veri Girişi.
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { KpiDisclosureSummary } from "@mahir/shared";
import { ApiError } from "../../api/client";
import { addKpiSetIndicator } from "../../api/kpi";
import { getKpiDisclosure } from "../../api/kpi-targets";
import { KpiScreen } from "../../components/kpi/KpiScreen";
import { DisclosureCards } from "../../components/kpi/targets/DisclosureCards";
import { DisclosureTable } from "../../components/kpi/targets/DisclosureTable";
import { MissingFromSetList } from "../../components/kpi/targets/MissingFromSetList";
import { ErrorState, LoadingState } from "../../components/common/StateSurface";
import {
  disclosureRowMatches,
  KPI_TARGETS_COPY,
  type DisclosureTab,
} from "../../lib/kpi-targets-ui";
import { useAuthStore } from "../../stores/auth-store";
import { useKpiStore } from "../../stores/kpi-store";
import { useCreateSetNav } from "./use-create-set-nav";

const C = KPI_TARGETS_COPY.disclosure;

const btn: React.CSSProperties = {
  padding: "6px 12px",
  borderRadius: 8,
  border: "1px solid var(--color-border)",
  background: "var(--color-surface)",
  fontSize: "0.82rem",
  color: "var(--color-text)",
  minHeight: 34,
};

export function KpiDisclosurePage(): JSX.Element {
  const createSet = useCreateSetNav();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const canManage = user?.permissions["kpi.manage"] === true;
  const setId = useKpiStore((s) => s.setId);
  const grid = useKpiStore((s) => s.grid);
  const years = useKpiStore((s) => s.years);
  const [standard, setStandard] = useState<string | null>(null);
  const [year, setYear] = useState<number>(() => new Date().getFullYear());
  const [summary, setSummary] = useState<KpiDisclosureSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<DisclosureTab>("missing");

  const reload = useCallback(async () => {
    if (!setId) {
      setSummary(null);
      return;
    }
    try {
      const s = await getKpiDisclosure(setId, { ...(standard ? { standard } : {}), year });
      setSummary(s);
      setStandard(s.standard);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : C.loadFailed);
      setSummary(null);
    }
  }, [setId, standard, year]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const yearOptions = Array.from(new Set([...years, year, new Date().getFullYear()])).sort(
    (a, b) => b - a,
  );
  const rows = summary ? summary.rows.filter((r) => disclosureRowMatches(r, tab)) : [];
  const counts = summary
    ? {
        missing: summary.rows.filter((r) => disclosureRowMatches(r, "missing")).length,
        all: summary.rows.filter((r) => disclosureRowMatches(r, "all")).length,
        notInSet: summary.missingFromSet,
      }
    : { missing: 0, all: 0, notInSet: 0 };

  const tabBtn = (key: DisclosureTab, label: string, n: number): JSX.Element => (
    <button
      type="button"
      role="tab"
      aria-selected={tab === key}
      onClick={() => setTab(key)}
      className="px-3 py-2 text-sm"
      style={{
        fontWeight: 600,
        color: tab === key ? "var(--color-primary)" : "var(--color-text-2)",
        borderBottom: `2px solid ${tab === key ? "var(--color-primary)" : "transparent"}`,
        marginBottom: -1,
      }}
    >
      {label} ({n})
    </button>
  );

  return (
    <KpiScreen
      title={KPI_TARGETS_COPY.titles.disclosure}
      showYears={false}
      onCreateSet={() => createSet()}
      onCreateSetForClient={(id) => createSet(id)}
      right={
        <div className="flex items-center gap-2">
          <label
            className="text-xs flex items-center gap-1"
            style={{ color: "var(--color-text-2)" }}
          >
            {C.standardLabel}
            <select
              aria-label={C.standardLabel}
              style={btn}
              value={standard ?? ""}
              onChange={(e) => setStandard(e.target.value || null)}
            >
              {(summary?.standards ?? (standard ? [standard] : [])).map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </label>
          <label
            className="text-xs flex items-center gap-1"
            style={{ color: "var(--color-text-2)" }}
          >
            {C.yearLabel}
            <select
              aria-label={C.yearLabel}
              style={btn}
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
            >
              {yearOptions.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </label>
        </div>
      }
    >
      {error ? (
        <ErrorState message={error} />
      ) : !summary ? (
        <LoadingState label="Disclosure özeti yükleniyor…" />
      ) : (
        <div className="min-w-0">
          <p className="text-sm mb-3" style={{ color: "var(--color-text-2)", maxWidth: 760 }}>
            {C.subtitle}
          </p>
          <DisclosureCards summary={summary} />
          <div
            role="tablist"
            className="flex gap-1 mb-0"
            style={{ borderBottom: "1px solid var(--color-border)" }}
          >
            {tabBtn("missing", C.tabs.missing, counts.missing)}
            {tabBtn("all", C.tabs.all, counts.all)}
            {tabBtn("notInSet", C.tabs.notInSet, counts.notInSet)}
          </div>
          <div
            className="rounded-b-xl"
            style={{
              background: "var(--color-surface)",
              border: "1px solid var(--color-border)",
              borderTop: 0,
            }}
          >
            {tab === "notInSet" ? (
              <MissingFromSetList
                rows={summary.rows}
                canAdd={canManage}
                onAdd={async (metricId) => {
                  if (!setId) return;
                  await addKpiSetIndicator(setId, { metricId });
                  await useKpiStore.getState().refreshGrid();
                  await reload();
                }}
              />
            ) : (
              <DisclosureTable
                rows={rows}
                tree={{
                  indicators: grid?.indicators ?? [],
                  allRows: summary.rows.filter((r) => r.status !== "not_in_set"),
                  filtered: tab !== "all",
                }}
                emptyText={tab === "missing" ? C.noneMissing : C.noStandards}
                onOpen={(code) =>
                  void navigate(`/clients/kpi/tablo?highlight=${encodeURIComponent(code)}`)
                }
              />
            )}
            <div
              className="text-xs px-3 py-2"
              style={{ color: "var(--color-text-3)", borderTop: "1px solid var(--color-border)" }}
            >
              {C.rowHint}
            </div>
          </div>
        </div>
      )}
    </KpiScreen>
  );
}
